import React from "react";
import { motion } from "framer-motion";
import { Brain, TrendUp, TrendDown, Target, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import { formatPct, formatSignedPct } from "../lib/format";

function Gauge({ value, label, color = "#00d8ff" }) {
  const pct = Math.max(0, Math.min(1, value || 0));
  const deg = pct * 180;
  return (
    <div className="flex flex-col items-center">
      <div
        className="relative w-32 h-16 overflow-hidden"
        style={{ background: `conic-gradient(from -90deg, ${color} ${deg}deg, rgba(255,255,255,0.08) ${deg}deg ${180}deg, transparent ${180}deg)` }}
      >
        <div className="absolute inset-2 bg-zinc-950 rounded-t-full" />
      </div>
      <div className="-mt-2 font-mono text-2xl tabular-nums font-medium">{formatPct(pct, 0)}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500">{label}</div>
    </div>
  );
}

export default function AIForecastPanel({ forecast, loading, error, streamText, onRefresh }) {
  if (loading) {
    const preview = streamText || "";
    // Try to extract a partial reasoning chunk if present
    let reasoningPreview = "";
    const m = preview.match(/"reasoning"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)/);
    if (m && m[1]) {
      reasoningPreview = m[1].replace(/\\n/g, "\n").replace(/\\"/g, '"');
    }
    const dotCount = Math.min(3, Math.floor(preview.length / 60));
    return (
      <div data-testid="ai-forecast-loading" className="border border-edge/40 ai-edge-ring p-6">
        <div className="flex items-center gap-3 text-edge">
          <Brain size={22} weight="duotone" className="animate-pulse-soft" />
          <div className="flex-1">
            <div className="font-display text-sm uppercase tracking-[0.22em]">
              MFSMAI Oracle · Thinking{".".repeat(dotCount)}
            </div>
            <div className="text-xs text-zinc-400 font-body mt-1">
              {reasoningPreview
                ? "Streaming reasoning live from MFSMAI Oracle…"
                : "Reasoning over base rates, market signals, and structural factors…"}
            </div>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-edge">
            {preview ? `${preview.length} chars` : ""}
          </span>
        </div>
        {reasoningPreview ? (
          <div
            data-testid="ai-stream-text"
            className="mt-5 border-l-2 border-edge pl-4 max-h-72 overflow-y-auto text-sm font-body text-zinc-200 leading-relaxed whitespace-pre-wrap"
          >
            {reasoningPreview}
            <span className="inline-block w-1.5 h-4 align-text-bottom bg-edge animate-pulse-soft ml-0.5" />
          </div>
        ) : (
          <div className="mt-5 space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-3 bg-white/5 animate-pulse-soft"
                style={{ width: `${100 - i * 12}%`, animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-500/40 p-6 text-sm text-red-300 font-body" data-testid="ai-forecast-error">
        AI forecast failed: {String(error)}
      </div>
    );
  }

  if (!forecast) {
    return null;
  }

  const verdictColor =
    forecast.verdict === "YES" ? "text-yes" : forecast.verdict === "NO" ? "text-no" : "text-zinc-300";
  const edge = Number(forecast.edge || 0);
  const edgeAbs = Math.abs(edge);
  const isEdge = edgeAbs >= 0.05;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="ai-forecast-panel"
      className={`relative border border-white/10 ${isEdge ? "ai-edge-ring" : ""} p-6 bg-zinc-950/60`}
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Brain size={22} weight="duotone" className="text-edge" />
          <div>
            <div className="font-display text-sm uppercase tracking-[0.22em] text-edge">
              MFSMAI Oracle Forecast
            </div>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5 uppercase tracking-widest">
              MFSMAI Oracle · {new Date(forecast.generatedAt).toLocaleString()}
            </div>
          </div>
        </div>
        <button
          onClick={onRefresh}
          data-testid="ai-refresh-btn"
          className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 px-3 py-1.5 transition-colors inline-flex items-center gap-1.5"
        >
          <ArrowsClockwise size={12} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 items-center">
        <Gauge value={forecast.probability} label="AI YES Probability" color="#00d8ff" />
        <div className="flex flex-col items-center">
          <div className={`font-display text-4xl font-semibold ${verdictColor}`}>
            {forecast.verdict}
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500 mt-1">
            Verdict
          </div>
          <div className="mt-3 text-xs font-mono text-zinc-400 tabular-nums">
            confidence · {formatPct(forecast.confidence, 0)}
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div
            className={`font-mono text-3xl tabular-nums font-medium ${
              edge > 0 ? "text-profit" : edge < 0 ? "text-loss" : "text-zinc-300"
            }`}
          >
            {formatSignedPct(edge, 1)}
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500 mt-1">
            AI Edge vs Market
          </div>
          <div className="text-xs text-zinc-500 font-body mt-2">
            Market: {formatPct(forecast.marketYesPrice, 0)} · AI: {formatPct(forecast.probability, 0)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="border border-white/10 p-4">
          <div className="flex items-center gap-2 mb-3 text-yes">
            <TrendUp size={16} weight="duotone" />
            <span className="font-display text-xs uppercase tracking-[0.22em]">
              Bullish (Pro-YES)
            </span>
          </div>
          <ul className="space-y-2 text-sm text-zinc-300 font-body">
            {(forecast.bullish || []).map((b, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-yes font-mono">+</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-white/10 p-4">
          <div className="flex items-center gap-2 mb-3 text-no">
            <TrendDown size={16} weight="duotone" />
            <span className="font-display text-xs uppercase tracking-[0.22em]">
              Bearish (Pro-NO)
            </span>
          </div>
          <ul className="space-y-2 text-sm text-zinc-300 font-body">
            {(forecast.bearish || []).map((b, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-no font-mono">−</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border border-white/10 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3 text-edge">
          <Target size={16} weight="duotone" />
          <span className="font-display text-xs uppercase tracking-[0.22em]">
            Key Factors to Watch
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {(forecast.keyFactors || []).map((k, i) => (
            <span
              key={i}
              className="text-xs font-body text-zinc-200 border border-white/15 px-2.5 py-1"
            >
              {k}
            </span>
          ))}
        </div>
      </div>

      <div className="border-l-2 border-edge pl-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={14} weight="fill" className="text-edge" />
          <span className="font-display text-xs uppercase tracking-[0.22em] text-zinc-400">
            Reasoning
          </span>
        </div>
        <p className="text-sm text-zinc-300 font-body leading-relaxed">{forecast.reasoning}</p>
      </div>

      <div className="mt-5 text-[10px] text-zinc-600 font-body italic">
        Not financial advice. AI forecasts are model estimates and can be wrong.
      </div>
    </motion.div>
  );
}
